import React, { useContext, useEffect, useState } from "react";
import styles from "./Profile.module.scss";

import Grid from "@material-ui/core/Grid";

import AppContext from "context/AppContext";
import {
  GitHub,
  EditRounded,
  DashboardRounded,
  BeenhereRounded,
  ViewDayRounded,
  Twitter,
  PublicRounded,
  LinkedIn,
  Facebook,
  Instagram,
  YouTube,
} from "@material-ui/icons";
import apiCalls from "shared/utils/apiCalls";
import { useParams } from "react-router-dom";
import Skeleton from "@material-ui/lab/Skeleton";
import NotFound from "shared/components/NotFound/NotFound";
import BadgeSmallSummary from "shared/components/BadgeSmallSummary/BadgeSmallSummary";
import Button from "shared/components/Buttons/Button";

import Tabs from "shared/components/Tabs/Tabs";
import ProfileSolutions from "./ProfileSolutions/ProfileSolutions";
import ProfileOverview from "./ProfileOverview/ProfileOverview";
import ProfileCertificates from "./ProfileCertificates/ProfileCertificates";

enum TabValues {
  overview = "overview",
  solutions = "solutions",
  certificates = "certificates",
}

const Profile = () => {
  const { state, dispatch } = useContext(AppContext);
  const [solutions, setSolutions] = useState<any>();
  const [user, setUser] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [certificates, setCertificates] = useState<any>();
  const [userBadges, setUserBadges] = useState<any>();
  const [tab, setTab] = useState<TabValues>(TabValues.overview);
  const [skills, setSkills] = useState<any>([]);
  const [gettingSkills, setGettingSkills] = useState<boolean>(true);
  const [recentFeedbacks, setRecentFeedbacks] = useState<any>([]);

  const tabs = [
    {
      value: TabValues.overview,
      label: "Overview",
      Icon: DashboardRounded,
    },
    {
      value: TabValues.solutions,
      label: `Solutions (${solutions?.length || 0})`,
      Icon: ViewDayRounded,
    },
    {
      value: TabValues.certificates,
      label: `Certificates (${certificates?.length || 0})`,
      Icon: BeenhereRounded,
    },
  ];

  const { id } = useParams();

  const getUserCertificate = async (uid: string) => {
    const res = await apiCalls({
      method: "get",
      url: `/certificate/user/${uid}`,
    });

    if (res) {
      setCertificates(res.data);
    }
  };

  const getUserBadges = async (uid: string) => {
    const res = await apiCalls({
      method: "get",
      url: `/user-badges/${uid}`,
    });

    if (res) {
      setUserBadges(res.data);
    }
  };

  const getRecentFeedbacks = async (uid: string) => {
    const res = await apiCalls({
      method: "get",
      url: `/feedbacks/user/${uid}`,
    });

    if (res) {
      setRecentFeedbacks(res.data);
    }
  };

  const reportUser = async (uid: string) => {
    if (!user) {
      return;
    }

    const isSure = await confirmModal();

    if (!isSure) {
      return;
    }

    const res = await apiCalls({
      method: "post",
      data: {
        uid: user.uid,
      },
      url: `/report/user-badges`,
    });

    if (res) {
      alert('Reported');
      window.location.reload();
    }
  };


  const confirmModal = () => {
    return new Promise((resolve) => {
      if (window.confirm("Are you sure?")) {
        resolve(true);
      } else {
        resolve(false);
      }

    });
  }

  const deleteUser = async (uid: string) => {
    if (!user) {
      return;
    }

    const isSure = await confirmModal();

    if (!isSure) {
      return;
    }

    const res = await apiCalls({
      method: "delete",
      url: `/user/${uid}`,
    });

    if (res) {
      alert('Deleted');
    }
  };
  useEffect(() => {
    const getUser = async () => {
      const userRes = await apiCalls(
        {
          method: "get",
          url: `/users/${id}`,
        },
        dispatch
      );

      if (userRes) {
        setUser(userRes.data);

        getSolutions(userRes.data.uid);
        getUserCertificate(userRes.data.uid);
        getUserBadges(userRes.data.uid);
        getUserSkillsAtStart(userRes.data.uid);
        getRecentFeedbacks(userRes.data.uid);
      }

      setLoading(false);
    };

    const getSolutions = async (uid: string) => {
      const res = await apiCalls({
        method: "get",
        url: `/solutions/user/${uid}?filter=submitted`,
      });

      if (res) {
        setSolutions(res.data);
      }
    };

    getUser();

    // eslint-disable-next-line
  }, [id]);

  const getUserSkillsAtStart = async (uid: string) => {
    setGettingSkills(true);
    if (uid) {
      const res = await apiCalls(
        {
          method: "get",
          url: `/skills/user/${uid}`,
        },
        dispatch
      );

      setSkills(res?.data);
    }

    setGettingSkills(false);
  };

  const getUserSkills = async (uid: string) => {
    if (uid) {
      const res = await apiCalls(
        {
          method: "get",
          url: `/skills/user/${uid}`,
        },
        dispatch
      );

      setSkills(res?.data);
    }
  };

  if (!loading && !user?.uid) {
    return <NotFound />;
  }

  return (
    <div className={styles.profile}>
      {user ? (
        <Grid
          container
          direction="row"
          spacing={2}
          alignItems="stretch"
          justify="center"
        >
          <Grid item xs={12} md={7}>
            <div className={styles.profile_user_summary}>
              <div className={styles.profile_user_summary_imgWrapper}>
                <img loading="lazy" src={user.picture} alt={user.name} />
              </div>

              <div className={styles.profile_user_summary_details}>
                <h3 style={{ marginBottom: 0 }}>{user.name}</h3>

                <div
                  style={{ marginBottom: "var(--space-sm)" }}
                  className={styles.profile_user_summary_badges}
                >
                  <span
                    className={styles.profile_user_summary_badges_points}
                    style={{
                      marginTop: "var(--space-xs)",
                      marginBottom: "var(--space-2xs)",
                    }}
                  >
                    <b>{user.reputations || 0}</b> reputations
                  </span>

                  {userBadges?.length > 0 && (
                    <>
                      <span className={styles.profile_user_summary_badges_dot}>
                        &middot;
                      </span>
                      <BadgeSmallSummary badges={userBadges} />
                    </>
                  )}
                </div>

                <div className={styles.profile_user_summary_achievements}>
                  <div
                    className={
                      styles.profile_user_summary_achievements_challenges
                    }
                  >
                    <span>{user?.solutions?.length | 0}</span>
                    <label>Solutions</label>
                  </div>

                  <div
                    className={
                      styles.profile_user_summary_achievements_certificates
                    }
                  >
                    <span>{certificates?.length | 0}</span>
                    <label>Certificates</label>
                  </div>
                </div>

                <div>
                  <Button
                    size="small"
                    buttonVariant="secondary"
                    disableElevation
                    onClick={() =>
                      window.open(
                        `https://portfolio.devchallenges.io/${user.githubUrl.replace(
                          "https://github.com/",
                          ""
                        )}`,
                        "_blank"
                      )
                    }
                  >
                    My Portfolio
                  </Button>
                </div>
              </div>
            </div>
          </Grid>

          <Grid item xs={12} md={5}>
            <div className={styles.profile_bio}>
              <div className={styles.profile_bio_heading}>
                <h4>About me</h4>

                {user.uid === state.user?.uid && (
                  <a
                    className={styles.profile_bio_heading_editButton}
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://github.com/settings/profile"
                  >
                    <EditRounded fontSize="inherit" />
                  </a>
                )}
              </div>

              <p>{user.bio}</p>

              <div className={styles.profile_bio_links}>
                {user.blog && (
                  <div className={styles.profile_bio_links_link}>
                    <a
                      href={
                        user.blog.indexOf("http") > -1
                          ? user.blog
                          : `http://${user.blog}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <PublicRounded fontSize="inherit" />
                    </a>
                  </div>
                )}
                {user.githubUrl && (
                  <div className={styles.profile_bio_links_link}>
                    <a
                      href={user.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <GitHub fontSize="inherit" />
                    </a>
                  </div>
                )}

                {user.linkedin && user.linkedin.length > 0 && (
                  <div className={styles.profile_bio_links_link}>
                    <a
                      href={user.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedIn fontSize="inherit" />
                    </a>
                  </div>
                )}

                {user.twitter && user.twitter.length > 0 && (
                  <div className={styles.profile_bio_links_link}>
                    <a
                      href={user.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Twitter fontSize="inherit" />
                    </a>
                  </div>
                )}

                {user.facebook && user.facebook.length > 0 && (
                  <div className={styles.profile_bio_links_link}>
                    <a
                      href={user.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Facebook fontSize="inherit" />
                    </a>
                  </div>
                )}
                {user.instagram && user.instagram.length > 0 && (
                  <div className={styles.profile_bio_links_link}>
                    <a
                      href={user.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Instagram fontSize="inherit" />
                    </a>
                  </div>
                )}
                {user.youtube && user.youtube.length > 0 && (
                  <div className={styles.profile_bio_links_link}>
                    <a
                      href={user.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <YouTube fontSize="inherit" />
                    </a>
                  </div>
                )}
                {user.twitch && user.twitch.length > 0 && (
                  <div className={styles.profile_bio_links_link}>
                    <a
                      href={user.twitch}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="var(--grey-6)"
                      >
                        <path
                          d="M2.149 0l-1.612 4.119v16.836h5.731v3.045h3.224l3.045-3.045h4.657l6.269-6.269v-14.686h-21.314zm19.164 13.612l-3.582 3.582h-5.731l-3.045 3.045v-3.045h-4.836v-15.045h17.194v11.463zm-3.582-7.343v6.262h-2.149v-6.262h2.149zm-5.731 0v6.262h-2.149v-6.262h2.149z"
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      ) : (
          <Grid
            container
            direction="row"
            spacing={2}
            alignItems="stretch"
            justify="center"
          >
            <Grid item xs={12} md={7}>
              <Skeleton variant="rect" height={188} />
            </Grid>

            <Grid item xs={12} md={5}>
              <Skeleton variant="rect" height={188} />
            </Grid>
          </Grid>
        )}

      <div className={styles.profile_certificatesAndChallenges}>
        <Tabs currentTabValue={tab} tabs={tabs} onTabSelected={setTab} />

        {tab === TabValues.solutions && (
          <ProfileSolutions solutions={solutions} />
        )}

        {tab === TabValues.overview && (
          <ProfileOverview
            userBadges={userBadges}
            skills={skills}
            getUserSkills={getUserSkills}
            user={user}
            gettingSkills={!!gettingSkills}
            recentFeedbacks={recentFeedbacks}
          />
        )}

        {tab === TabValues.certificates && (
          <ProfileCertificates certificates={certificates} />
        )}

        {state?.user?.role === "admin" && (
          <div className={styles.profile_actions}>
            <h4 style={{ marginBottom: "var(--space-xs)" }}>Danger area</h4>
            <p style={{ marginBottom: "var(--space-lg)" }}>
              These actions directly affect the user
            </p>
            <div style={{ marginTop: "var(--space-md)" }}>
              <Button onClick={() => reportUser(user?.uid)} aria-label="report">
                Report
              </Button>
            </div>

            <div style={{ marginTop: "var(--space-md)" }}>
              <Button onClick={() => deleteUser(user?.uid)} aria-label="delete">
                Delete
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
